import {
    getOwnedNodes,
    Camera,
    defaultsForToneMapping,
    MaterialAssignment,
    MaterialAssignments,
    MaterialReference,
    TemplateNodes,
    ProceduralMesh,
    Render,
    SceneNodes,
    TemplateGraph,
    buildRenderGraph,
    deserializeTemplateGraph,
} from "@cm/template-nodes"
import {Injector} from "@angular/core"
import type {CmLogger} from "@cm/log"
import {GetTemplateDetailsForMaterialAssetsRenderingGQL} from "@app/common/helpers/rendering/material/material-assets-rendering/template.generated"
import {DataResolverService} from "@app/material-editor/services/data-resolver.service"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {DataResolver, getPhysicalSizeInfoForMaterialGraph, MaterialGraph} from "@cm/material-nodes"
import {Matrix4} from "@cm/math"
import {RenderNodes} from "@cm/render-nodes"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {MaterialAssetsRenderingMaterialFragment} from "@common/services/material-assets-rendering/material-assets-rendering.generated"
import {CM_LOGGER_RENDERING} from "@common/services/logging"

export const MAX_PIXEL_PER_MM = 32.9
export const CM_TO_M = 0.01
export const CM_TO_MM = 10
export const CM_TO_INCH = 0.393701

type NonNullableOverRootFields<T extends {[key: string]: unknown}> = {[K in keyof T]: NonNullable<T[K]>}

export type LegacyId = number
export type MaterialDetails = NonNullableOverRootFields<Required<Omit<MaterialAssetsRenderingMaterialFragment, "__typename">>>
export type JobGraphMaterialDetails = Omit<MaterialDetails, "latestCyclesRevision">

export async function renderGraphForTemplateGraph(sceneNodes: SceneNodes.SceneNode[], injector: Injector): Promise<RenderNodes.Render> {
    const dataResolver = injector.get(DataResolverService)
    const logger = injector.get(CM_LOGGER_RENDERING)
    return buildRenderGraph(
        {nodes: sceneNodes, fullQuality: true, renderPurpose: "final", verifySchema: true, passes: ["Combined"]},
        dataResolver.resolve,
        logger,
    )
}

export const getLatestTemplateGraph = async (injector: Injector, templateId: LegacyId) => {
    const getTemplateDetailsForMaterialAssetsRendering = injector.get(GetTemplateDetailsForMaterialAssetsRenderingGQL)
    const templateDetails = (await fetchThrowingErrors(getTemplateDetailsForMaterialAssetsRendering)({legacyId: templateId})).template
    if (!templateDetails.latestRevision) throw new Error("Missing latest revision!")
    if (!templateDetails.latestRevision.graph) throw new Error("Missing graph in latest revision")

    return deserializeTemplateGraph(templateDetails.latestRevision.graph)
}

export async function setupTemplateGraphForPlaneRender(
    materialId: LegacyId,
    materialRevisionId: LegacyId | null,
    extensionTemplateId: LegacyId,
    resolutionX: number,
    resolutionY: number,
    samples: number,
    planeX: number,
    planeY: number,
    injector: Injector,
    sceneManagerService: SceneManagerService,
    matOffsetX = 0,
    matOffsetY = 0,
    matRot = 0,
): Promise<SceneNodes.SceneNode[]> {
    const fieldOfView = 0.005 //Smaller FOV moves the camera further away from the plane when generating thumbnails & tileable images. At distances > ~100km, we get rendering artifacts.
    const sensorSize = 36

    const focalLength = getFocalLengthFromFOVAndSensorSize(fieldOfView, sensorSize)
    const cameraDistanceToPlane = (focalLength / sensorSize) * Math.max(planeX, planeY)

    const materialReference = new MaterialReference({parameters: {name: "Material Reference", materialId, materialRevisionId}})

    const plane_padding = 20.0

    const templateGraph = new TemplateGraph({
        parameters: {
            name: "Untitled Scene",
            nodes: new TemplateNodes({
                parameters: {
                    list: [
                        new ProceduralMesh({
                            parameters: {
                                name: "Studio",
                                geometryGraph: "plane",
                                materialAssignments: new MaterialAssignments({
                                    parameters: {
                                        "0": new MaterialAssignment({
                                            parameters: {
                                                node: materialReference,
                                                horizontalOffset: matOffsetX,
                                                verticalOffset: matOffsetY,
                                                rotation: matRot,
                                                side: "front",
                                            },
                                        }),
                                    },
                                }),
                                materialSlotNames: {"0": "Floor"},
                                parameters: {
                                    width: planeX + plane_padding,
                                    height: planeY + plane_padding,
                                },
                                lockedTransform: Matrix4.identity().toArray(),
                                visible: true,
                                visibleDirectly: true,
                                visibleInReflections: true,
                                visibleInRefractions: true,
                                castRealtimeShadows: true,
                                receiveRealtimeShadows: true,
                            },
                        }),
                        new Camera({
                            parameters: {
                                name: "Main Camera",
                                resolutionX,
                                resolutionY,
                                lockedTransform: Matrix4.translation(0, cameraDistanceToPlane, 0).multiply(Matrix4.rotationX(-90)).toArray(),
                                target: [0, 0, 0],
                                filmGauge: sensorSize,
                                focalLength: focalLength,
                                focalDistance: cameraDistanceToPlane,
                                fStop: 16,
                                shiftX: 0,
                                shiftY: 0,
                                automaticVerticalTilt: false,
                                enableOrbiting: true,
                                enablePanning: true,
                                screenSpacePanning: true,
                                adaptAspectRatio: true,
                                minDistance: 0,
                                maxDistance: 10000,
                                minPolarAngle: 0,
                                maxPolarAngle: 180,
                                targeted: false,
                                autoFocus: false,
                                toneMapping: defaultsForToneMapping("linear"),
                                ev: Math.log2(1),
                                visible: true,
                                enableDamping: false,
                                dampingFactor: 0.05,
                            },
                        }),
                        new Render({
                            parameters: {
                                percentage: 100,
                                samples: samples,
                                textureStreaming: false,
                                renderTiling: "auto",
                                denoiseTiling: "auto",
                            },
                        }),
                        ...getOwnedNodes(await getLatestTemplateGraph(injector, extensionTemplateId)),
                    ],
                },
            }),
        },
    })

    sceneManagerService.$templateGraph.set(templateGraph)
    sceneManagerService.compileTemplate()
    await sceneManagerService.sync(true)
    return sceneManagerService.$scene()
}

export function getFocalLengthFromFOVAndSensorSize(FOVInDeg: number, sensorSize: number): number {
    return sensorSize / (2 * Math.atan(((FOVInDeg / 2) * Math.PI) / 180))
}

export async function tileParamsForMaterial(materialGraph: MaterialGraph, resolve: DataResolver, logger: CmLogger) {
    const info = await getPhysicalSizeInfoForMaterialGraph(materialGraph, resolve, logger)

    if (!info) throw Error(`Could not determine physical size for material {material.id}`)
    const repeatWidth = Math.round(info.pxPerCm * info.widthCm)
    const repeatHeight = Math.round(info.pxPerCm * info.heightCm)
    return {repeatWidthCm: info.widthCm, repeatHeightCm: info.heightCm, repeatWidth, repeatHeight, pxPerCm: info.pxPerCm}
}
